// Utilidades de la app «Deals» (sin estado ni red).
//
// 🔒 Dinero: este archivo SOLO FORMATEA importes que entrega el servidor
// (value_amount, weighted_value, totales del forecast). Nunca suma ni calcula
// totales de dinero en el cliente.

import type { DealStage, DealStageCreateIn, FollowUpState } from './types';

const TZ = 'America/Mexico_City';

export function fmtMoney(amount: string | number | null | undefined, currency = 'MXN'): string {
  if (amount === null || amount === undefined || amount === '') return '—';
  const n = Number(amount);
  if (Number.isNaN(n)) return `${amount} ${currency}`;
  try {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency || 'MXN', maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${n.toLocaleString('es-MX')} ${currency}`;
  }
}

/** Importe corto para encabezados angostos: «$1.2 M», «$85 k». Solo formato. */
export function fmtMoneyShort(amount: string | number | null | undefined, currency = 'MXN'): string {
  const n = Number(amount);
  if (amount === null || amount === undefined || Number.isNaN(n)) return '—';
  const sym = ['MXN', 'USD', 'CAD', 'AUD'].includes(currency) ? '$' : '';
  const tail = sym ? '' : ` ${currency}`;
  const abs = Math.abs(n);
  const num = abs >= 1e6
    ? `${(n / 1e6).toLocaleString('es-MX', { maximumFractionDigits: 1 })} M`
    : abs >= 1e4
      ? `${Math.round(n / 1e3).toLocaleString('es-MX')} k`
      : abs >= 1e3
        ? `${(n / 1e3).toLocaleString('es-MX', { maximumFractionDigits: 1 })} k`
        : n.toLocaleString('es-MX', { maximumFractionDigits: 0 });
  return `${sym}${num}${tail}`;
}

/** Fecha civil AAAA-MM-DD (sin hora) → «12 nov» / «12 nov 2027». */
export function fmtDay(ymd: string | null | undefined, withYear = false): string {
  if (!ymd) return '';
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  const thisYear = new Date().getUTCFullYear();
  return dt.toLocaleDateString('es-MX', {
    day: 'numeric', month: 'short', ...(withYear || y !== thisYear ? { year: 'numeric' } : {}), timeZone: 'UTC',
  });
}

/** «2027-03» → «marzo 2027». */
export function fmtMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym || 'Sin fecha';
  return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString('es-MX', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: TZ });
}

/** Hoy en el día civil de la org (MX). */
export function todayCivil(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: TZ });
}

export function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n, 12));
  return dt.toISOString().slice(0, 10);
}

// ── Semáforo de seguimiento (lo calcula el backend; aquí solo se pinta) ──

export const FOLLOW_UP: Record<string, { label: string; tone: string }> = {
  rojo_vencido: { label: 'Vencido', tone: 'dl-fu-overdue' },
  rojo: { label: 'Hoy toca', tone: 'dl-fu-today' },
  verde: { label: 'Agendado', tone: 'dl-fu-ok' },
  sin_fecha: { label: 'Sin fecha', tone: 'dl-fu-none' },
  en_pausa: { label: 'En pausa', tone: 'dl-fu-none' },
  cerrado: { label: 'Cerrado', tone: 'dl-fu-none' },
};

export function followUpLabel(state: FollowUpState | string, next: string, overdue: number): string {
  if (state === 'rojo_vencido') return overdue === 1 ? 'Vencido hace 1 día' : `Vencido hace ${overdue} días`;
  if (state === 'rojo') return 'Contactar hoy';
  if (state === 'verde' && next) return `Contactar ${fmtDay(next)}`;
  if (state === 'en_pausa') return 'En pausa (3 sin respuesta)';
  if (state === 'sin_fecha') return 'Sin próximo contacto';
  return FOLLOW_UP[state]?.label || '';
}

export const OUTCOME_LABEL: Record<string, string> = {
  reagendar: 'Reagendó', ganada: 'Ganada', perdida: 'Perdida', no_contesto: 'No contestó',
};
export const CHANNEL_LABEL: Record<string, string> = {
  llamada: 'Llamada', whatsapp: 'WhatsApp', correo: 'Correo', presencial: 'Presencial', otro: 'Otro',
};

export const KIND_LABEL: Record<string, string> = { open: 'Abierta', won: 'Cierre ganado', lost: 'Cierre perdido' };

// ── Etapas ────────────────────────────────────────────────────

/** Color de la etapa (dato de la org). Solo se usa en marcas NO textuales
 *  (punto, borde superior de columna); el texto va siempre en tokens. */
export function stageColor(s: Pick<DealStage, 'color' | 'kind'> | undefined): string {
  if (s?.color && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s.color)) return s.color;
  if (s?.kind === 'won') return '#10B981';
  if (s?.kind === 'lost') return '#EF4444';
  return 'hsl(var(--primary))';
}

export function sortStages(stages: DealStage[]): DealStage[] {
  return [...stages].filter((s) => s.active !== false).sort((a, b) => a.order - b.order || a.name.localeCompare(b.name, 'es'));
}

/** Plantilla sugerida al configurar un tablero vacío (la confirma el admin). */
export const STAGE_TEMPLATE: DealStageCreateIn[] = [
  { slug: 'nuevo', name: 'Nuevo', order: 10, default_probability: 10, kind: 'open', color: '#94A3B8' },
  { slug: 'contactado', name: 'Contactado', order: 20, default_probability: 25, kind: 'open', color: '#60A5FA' },
  { slug: 'cotizacion', name: 'Cotización', order: 30, default_probability: 50, kind: 'open', color: '#A78BFA' },
  { slug: 'negociacion', name: 'Negociación', order: 40, default_probability: 75, kind: 'open', color: '#F59E0B' },
  { slug: 'ganado', name: 'Ganado', order: 90, default_probability: 100, kind: 'won', color: '#10B981' },
  { slug: 'perdido', name: 'Perdido', order: 99, default_probability: 0, kind: 'lost', color: '#EF4444' },
];

export const STAGE_COLORS = ['#94A3B8', '#60A5FA', '#A78BFA', '#8B5CF6', '#F59E0B', '#F97316', '#10B981', '#EF4444'];

export function slugify(name: string): string {
  return name
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .slice(0, 50) || `etapa-${Date.now().toString(36)}`;
}

export function readPref(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
export function writePref(key: string, v: string) {
  try { window.localStorage.setItem(key, v); } catch { /* sin almacenamiento: no pasa nada */ }
}
